import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

import demoProducts from '../pages/Dashboard/LossControl/data/products.fake.json';
import { api, apiPublic } from './api';

export interface LossControlBranch {
  id: string;
  name: string;
}

export interface ExpirationItem {
  id: string;
  branchId: string;
  branchName: string;
  productId: number;
  productCode: string;
  productName: string;
  familyCode?: string;
  familyName?: string;
  groupCode?: string;
  groupName?: string;
  subgroupCode?: string | null;
  subgroupName?: string | null;
  ean?: string | null;
  lot: string;
  expiryDate: string;
  quantity: number;
  unit?: string | null;
  updatedAt?: string | null;
}

export interface LossControlDetailItem extends ExpirationItem {
  familyCode: string;
  familyName: string;
  groupCode: string;
  groupName: string;
  subgroupCode: string | null;
  subgroupName: string | null;
}

export interface ExpirationResponse {
  items: ExpirationItem[];
  generatedAt: string;
  sourceUpdatedAt?: string | null;
  sourceAvailable: boolean;
  demo?: boolean;
}

export interface DisplayAccessResponse {
  id?: string;
  branchId?: string;
  deviceName?: string;
  token: string;
  neverExpires?: boolean;
  expiresAt?: string | null;
}

export interface LossControlDisplayDevice {
  id: string;
  branchId: string;
  deviceName: string;
  neverExpires: boolean;
  active: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDisplayAccessOptions {
  deviceName?: string;
  neverExpires?: boolean;
}

export interface LossControlIndicators {
  expired: number;
  dueToday: number;
  dueIn1To7Days: number;
  dueIn8To15Days: number;
  dueIn16To30Days: number;
  dueAfter30Days: number;
  total: number;
}

export interface LossControlSummaryOperator {
  accessId: string;
  name: string;
  displayOrder: number;
  indicators: LossControlIndicators;
}

export interface LossControlSummary {
  branchId: string;
  branchName: string;
  generatedAt: string;
  sourceUpdatedAt: string | null;
  totalUniqueLots: number;
  totalOperators: number;
  indicators: LossControlIndicators;
  operators: LossControlSummaryOperator[];
  unassigned: {
    label: string;
    visible: boolean;
    displayOrder: number;
    indicators: LossControlIndicators;
  };
}

export type LossControlDetailsStatus =
  | 'all'
  | 'expired'
  | 'due_today'
  | 'due_1_7'
  | 'due_8_15'
  | 'due_16_30'
  | 'due_after_30';

export interface LossControlSubgroupSummary {
  subgroupCode: string | null;
  subgroupName: string | null;
  totalUniqueLots: number;
}

export interface LossControlGroupSummary {
  groupCode: string;
  groupName: string;
  totalUniqueLots: number;
  subgroups: LossControlSubgroupSummary[];
}

export interface LossControlFamilySummary {
  familyCode: string;
  familyName: string;
  totalUniqueLots: number;
  groups: LossControlGroupSummary[];
}

export interface LossControlDetails {
  branchId: string;
  branchName: string;
  generatedAt: string;
  sourceUpdatedAt: string | null;
  subject: {
    type: 'all' | 'operator' | 'unassigned';
    accessId: string | null;
    name: string;
  };
  statusFilter: LossControlDetailsStatus;
  totalUniqueLots: number;
  filteredTotal: number;
  indicators: LossControlIndicators;
  classifications: LossControlFamilySummary[];
  items: LossControlDetailItem[];
}

export interface LossControlClassification {
  code: string;
  name: string;
}

export interface LossControlResponsibilityScope {
  id: string;
  branchCode: string;
  branchName: string;
  familyCode: string;
  familyName: string;
  groupCode: string | null;
  groupName: string | null;
  subgroupCode: string | null;
  subgroupName: string | null;
}

export interface LossControlOperatorResponsibility {
  assignmentId: string;
  accessId: string;
  active: boolean;
  scope: LossControlResponsibilityScope;
}

export interface LossControlRequestedScope {
  branchCode: string;
  branchName: string;
  familyCode: string;
  familyName: string;
  groupCode?: string | null;
  groupName?: string | null;
  subgroupCode?: string | null;
  subgroupName?: string | null;
}

interface ExpirationFilters {
  branchId?: string;
  horizonDays?: number;
}

interface SummaryFilters {
  branchId: string;
  horizonDays?: number;
  familyCode?: string;
  groupCode?: string;
  subgroupCode?: string;
}

interface DetailsFilters extends SummaryFilters {
  status?: LossControlDetailsStatus;
}

const demoBranches: LossControlBranch[] = [
  { id: '1', name: 'Mercado Matriz' },
  { id: '2', name: 'Mercado Rio Verde' },
];

const demoItems = demoProducts as ExpirationItem[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isValidISODate(value: unknown): value is string {
  return typeof value === 'string' && isValid(parseISO(value));
}

function requiredString(value: unknown): string {
  return String(value ?? '').trim();
}

function nullableString(value: unknown): string | null {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function nonNegativeInteger(value: unknown): number | null {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized >= 0 ? normalized : null;
}

function normalizeExpirationItem(value: unknown): ExpirationItem | null {
  if (!isRecord(value)) return null;

  const productId = Number(value.productId);
  const quantity = Number(value.quantity);
  const branchId = requiredString(value.branchId);
  const productName = requiredString(value.productName);
  const lot = requiredString(value.lot);

  if (
    !Number.isFinite(productId) ||
    productId <= 0 ||
    !Number.isFinite(quantity) ||
    quantity < 0 ||
    !branchId ||
    !productName ||
    !lot ||
    !isValidISODate(value.expiryDate)
  ) {
    return null;
  }

  const productCode = requiredString(value.productCode ?? productId);
  const fallbackId = `${branchId}-${productId}-${lot}-${value.expiryDate}`;

  return {
    id: requiredString(value.id ?? fallbackId),
    branchId,
    branchName: requiredString(value.branchName ?? `Loja ${branchId}`),
    productId,
    productCode,
    productName,
    familyCode: nullableString(value.familyCode) || undefined,
    familyName: nullableString(value.familyName) || undefined,
    groupCode: nullableString(value.groupCode) || undefined,
    groupName: nullableString(value.groupName) || undefined,
    subgroupCode: nullableString(value.subgroupCode),
    subgroupName: nullableString(value.subgroupName),
    ean: nullableString(value.ean),
    lot,
    expiryDate: value.expiryDate,
    quantity,
    unit: nullableString(value.unit),
    updatedAt: isValidISODate(value.updatedAt) ? value.updatedAt : null,
  };
}

function normalizeExpirationItems(value: unknown): ExpirationItem[] {
  if (!Array.isArray(value)) {
    throw new Error('A API nao retornou uma lista de vencimentos valida.');
  }

  const normalizedItems = value.map(normalizeExpirationItem);
  if (normalizedItems.some(item => item === null)) {
    throw new Error('A API retornou um vencimento incompleto ou invalido.');
  }

  return normalizedItems.filter(
    (item): item is ExpirationItem => item !== null,
  );
}

function normalizeDetailItem(value: unknown): LossControlDetailItem | null {
  const item = normalizeExpirationItem(value);
  if (!item || !isRecord(value)) return null;

  const familyCode = requiredString(value.familyCode);
  const familyName = requiredString(value.familyName);
  const groupCode = requiredString(value.groupCode);
  const groupName = requiredString(value.groupName);

  if (!familyCode || !familyName || !groupCode || !groupName) return null;

  return {
    ...item,
    familyCode,
    familyName,
    groupCode,
    groupName,
    subgroupCode: nullableString(value.subgroupCode),
    subgroupName: nullableString(value.subgroupName),
  };
}

function isArrayResponse<T>(value: unknown): value is T[] {
  return Array.isArray(value);
}

function normalizeBranches(payload: unknown): LossControlBranch[] {
  let rawBranches: unknown[] = [];

  if (isArrayResponse<unknown>(payload)) {
    rawBranches = payload;
  } else if (isRecord(payload) && isArrayResponse<unknown>(payload.items)) {
    rawBranches = payload.items;
  } else {
    throw new Error('A API nao retornou uma lista de lojas valida.');
  }

  return rawBranches
    .filter(isRecord)
    .map(branch => ({
      id: requiredString(branch.id),
      name: requiredString(branch.name),
    }))
    .filter(branch => branch.id && branch.name);
}

function normalizeDisplayDevice(value: unknown): LossControlDisplayDevice {
  if (!isRecord(value)) {
    throw new Error('A API retornou um dispositivo de TV invalido.');
  }

  const id = requiredString(value.id);
  const branchId = requiredString(value.branchId);
  const deviceName = requiredString(value.deviceName);

  if (
    !id ||
    !branchId ||
    !deviceName ||
    typeof value.neverExpires !== 'boolean' ||
    typeof value.active !== 'boolean' ||
    !isValidISODate(value.createdAt) ||
    !isValidISODate(value.updatedAt)
  ) {
    throw new Error('A API retornou um dispositivo de TV incompleto.');
  }

  const expiresAt = isValidISODate(value.expiresAt) ? value.expiresAt : null;
  const revokedAt = isValidISODate(value.revokedAt) ? value.revokedAt : null;

  if (!value.neverExpires && !expiresAt) {
    throw new Error('A API retornou a validade da TV incompleta.');
  }

  return {
    id,
    branchId,
    deviceName,
    neverExpires: value.neverExpires,
    active: value.active,
    expiresAt,
    revokedAt,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
  };
}

function normalizeDisplayDevices(payload: unknown): LossControlDisplayDevice[] {
  if (!Array.isArray(payload)) {
    throw new Error('A API nao retornou uma lista de TVs valida.');
  }

  return payload.map(normalizeDisplayDevice);
}

function normalizeIndicators(value: unknown): LossControlIndicators {
  if (!isRecord(value)) {
    throw new Error('A API retornou indicadores invalidos.');
  }

  const indicators = {
    expired: nonNegativeInteger(value.expired),
    dueToday: nonNegativeInteger(value.dueToday),
    dueIn1To7Days: nonNegativeInteger(value.dueIn1To7Days),
    dueIn8To15Days: nonNegativeInteger(value.dueIn8To15Days),
    dueIn16To30Days: nonNegativeInteger(value.dueIn16To30Days),
    dueAfter30Days: nonNegativeInteger(value.dueAfter30Days),
    total: nonNegativeInteger(value.total),
  };

  if (Object.values(indicators).some(item => item === null)) {
    throw new Error('A API retornou indicadores invalidos.');
  }

  return indicators as LossControlIndicators;
}

export function normalizeSummary(payload: unknown): LossControlSummary {
  if (!isRecord(payload)) {
    throw new Error('A API retornou um resumo invalido.');
  }

  const branchId = requiredString(payload.branchId);
  const branchName = requiredString(payload.branchName);
  const totalUniqueLots = nonNegativeInteger(payload.totalUniqueLots);
  const totalOperators = nonNegativeInteger(payload.totalOperators);

  if (
    !branchId ||
    !branchName ||
    totalUniqueLots === null ||
    totalOperators === null ||
    !Array.isArray(payload.operators) ||
    !isRecord(payload.unassigned)
  ) {
    throw new Error('A API retornou um resumo incompleto ou invalido.');
  }

  const operators = payload.operators.map(operator => {
    if (!isRecord(operator)) {
      throw new Error('A API retornou um operador invalido.');
    }

    const accessId = requiredString(operator.accessId);
    const name = requiredString(operator.name);
    const displayOrder = nonNegativeInteger(operator.displayOrder);

    if (!accessId || !name || displayOrder === null) {
      throw new Error('A API retornou um operador invalido.');
    }

    return {
      accessId,
      name,
      displayOrder,
      indicators: normalizeIndicators(operator.indicators),
    };
  });

  const displayOrder = nonNegativeInteger(payload.unassigned.displayOrder);
  const label = requiredString(payload.unassigned.label);
  if (!label || displayOrder === null) {
    throw new Error('A API retornou a categoria sem responsavel invalida.');
  }

  return {
    branchId,
    branchName,
    generatedAt: isValidISODate(payload.generatedAt)
      ? payload.generatedAt
      : new Date().toISOString(),
    sourceUpdatedAt: isValidISODate(payload.sourceUpdatedAt)
      ? payload.sourceUpdatedAt
      : null,
    totalUniqueLots,
    totalOperators,
    indicators: normalizeIndicators(payload.indicators),
    operators,
    unassigned: {
      label,
      visible: payload.unassigned.visible === true,
      displayOrder,
      indicators: normalizeIndicators(payload.unassigned.indicators),
    },
  };
}

function normalizeClassifications(value: unknown): LossControlFamilySummary[] {
  if (!Array.isArray(value)) {
    throw new Error('A API retornou classificacoes invalidas.');
  }

  return value.map(family => {
    if (!isRecord(family) || !Array.isArray(family.groups)) {
      throw new Error('A API retornou uma familia invalida.');
    }

    const familyCode = requiredString(family.familyCode);
    const familyName = requiredString(family.familyName);
    const totalUniqueLots = nonNegativeInteger(family.totalUniqueLots);
    if (!familyCode || !familyName || totalUniqueLots === null) {
      throw new Error('A API retornou uma familia invalida.');
    }

    return {
      familyCode,
      familyName,
      totalUniqueLots,
      groups: family.groups.map(group => {
        if (!isRecord(group) || !Array.isArray(group.subgroups)) {
          throw new Error('A API retornou um grupo invalido.');
        }

        const groupCode = requiredString(group.groupCode);
        const groupName = requiredString(group.groupName);
        const groupTotal = nonNegativeInteger(group.totalUniqueLots);
        if (!groupCode || !groupName || groupTotal === null) {
          throw new Error('A API retornou um grupo invalido.');
        }

        return {
          groupCode,
          groupName,
          totalUniqueLots: groupTotal,
          subgroups: group.subgroups.map(subgroup => {
            if (!isRecord(subgroup)) {
              throw new Error('A API retornou um subgrupo invalido.');
            }
            const subgroupTotal = nonNegativeInteger(subgroup.totalUniqueLots);
            if (subgroupTotal === null) {
              throw new Error('A API retornou um subgrupo invalido.');
            }
            return {
              subgroupCode: nullableString(subgroup.subgroupCode),
              subgroupName: nullableString(subgroup.subgroupName),
              totalUniqueLots: subgroupTotal,
            };
          }),
        };
      }),
    };
  });
}

export function normalizeDetails(payload: unknown): LossControlDetails {
  if (
    !isRecord(payload) ||
    !isRecord(payload.subject) ||
    !Array.isArray(payload.items)
  ) {
    throw new Error('A API retornou detalhes invalidos.');
  }

  const branchId = requiredString(payload.branchId);
  const branchName = requiredString(payload.branchName);
  const subjectType = payload.subject.type;
  const subjectName = requiredString(payload.subject.name);
  const statusFilter = payload.statusFilter as LossControlDetailsStatus;
  const totalUniqueLots = nonNegativeInteger(payload.totalUniqueLots);
  const filteredTotal = nonNegativeInteger(payload.filteredTotal);
  const validStatuses: LossControlDetailsStatus[] = [
    'all',
    'expired',
    'due_today',
    'due_1_7',
    'due_8_15',
    'due_16_30',
    'due_after_30',
  ];

  if (
    !branchId ||
    !branchName ||
    !subjectName ||
    (subjectType !== 'all' &&
      subjectType !== 'operator' &&
      subjectType !== 'unassigned') ||
    !validStatuses.includes(statusFilter) ||
    totalUniqueLots === null ||
    filteredTotal === null
  ) {
    throw new Error('A API retornou detalhes incompletos ou invalidos.');
  }

  const items = payload.items.map(normalizeDetailItem);
  if (items.some(item => item === null)) {
    throw new Error('A API retornou um produto detalhado invalido.');
  }

  return {
    branchId,
    branchName,
    generatedAt: isValidISODate(payload.generatedAt)
      ? payload.generatedAt
      : new Date().toISOString(),
    sourceUpdatedAt: isValidISODate(payload.sourceUpdatedAt)
      ? payload.sourceUpdatedAt
      : null,
    subject: {
      type: subjectType,
      accessId:
        subjectType === 'operator'
          ? nullableString(payload.subject.accessId)
          : null,
      name: subjectName,
    },
    statusFilter,
    totalUniqueLots,
    filteredTotal,
    indicators: normalizeIndicators(payload.indicators),
    classifications: normalizeClassifications(payload.classifications),
    items: items.filter((item): item is LossControlDetailItem => item !== null),
  };
}

function normalizeClassificationsList(
  payload: unknown,
): LossControlClassification[] {
  if (!Array.isArray(payload)) {
    throw new Error('A API nao retornou um catalogo valido.');
  }

  return payload.map(item => {
    if (!isRecord(item))
      throw new Error('A API retornou um catalogo invalido.');
    const code = requiredString(item.code);
    const name = requiredString(item.name);
    if (!code || !name) throw new Error('A API retornou um catalogo invalido.');
    return { code, name };
  });
}

function normalizeResponsibilities(
  payload: unknown,
): LossControlOperatorResponsibility[] {
  if (!Array.isArray(payload)) {
    throw new Error('A API nao retornou responsabilidades validas.');
  }

  return payload.map(item => {
    if (!isRecord(item) || !isRecord(item.scope)) {
      throw new Error('A API retornou uma responsabilidade invalida.');
    }

    const result: LossControlOperatorResponsibility = {
      assignmentId: requiredString(item.assignmentId),
      accessId: requiredString(item.accessId),
      active: item.active === true,
      scope: {
        id: requiredString(item.scope.id),
        branchCode: requiredString(item.scope.branchCode),
        branchName: requiredString(item.scope.branchName),
        familyCode: requiredString(item.scope.familyCode),
        familyName: requiredString(item.scope.familyName),
        groupCode: nullableString(item.scope.groupCode),
        groupName: nullableString(item.scope.groupName),
        subgroupCode: nullableString(item.scope.subgroupCode),
        subgroupName: nullableString(item.scope.subgroupName),
      },
    };

    if (
      !result.assignmentId ||
      !result.accessId ||
      !result.scope.id ||
      !result.scope.branchCode ||
      !result.scope.branchName ||
      !result.scope.familyCode ||
      !result.scope.familyName
    ) {
      throw new Error('A API retornou uma responsabilidade invalida.');
    }

    return result;
  });
}

export function normalizeExpirations(payload: unknown): ExpirationResponse {
  if (isArrayResponse<unknown>(payload)) {
    return {
      items: normalizeExpirationItems(payload),
      generatedAt: new Date().toISOString(),
      sourceAvailable: true,
    };
  }

  if (!isRecord(payload)) {
    throw new Error('A API retornou uma resposta de vencimentos invalida.');
  }

  return {
    items: normalizeExpirationItems(payload.items),
    generatedAt: isValidISODate(payload.generatedAt)
      ? payload.generatedAt
      : new Date().toISOString(),
    sourceUpdatedAt: isValidISODate(payload.sourceUpdatedAt)
      ? payload.sourceUpdatedAt
      : null,
    sourceAvailable: payload.sourceAvailable !== false,
  };
}

function demoDetailItems(branchId: string): LossControlDetailItem[] {
  return demoItems
    .filter(item => item.branchId === branchId)
    .map((item, index) => ({
      ...item,
      familyCode: '6',
      familyName: 'SUPERMERCADO',
      groupCode: index < 5 ? '1' : '2',
      groupName: index < 5 ? 'PERECIVEIS' : 'MERCEARIA',
      subgroupCode: null,
      subgroupName: null,
    }));
}

function filterDemoItems(
  items: LossControlDetailItem[],
  status: LossControlDetailsStatus,
): LossControlDetailItem[] {
  if (status === 'all') return items;

  return items.filter(item => {
    const days = differenceInCalendarDays(
      parseISO(item.expiryDate),
      new Date(),
    );
    if (status === 'expired') return days < 0;
    if (status === 'due_today') return days === 0;
    if (status === 'due_1_7') return days >= 1 && days <= 7;
    if (status === 'due_8_15') return days >= 8 && days <= 15;
    if (status === 'due_16_30') return days >= 16 && days <= 30;
    return days > 30;
  });
}

function demoIndicators(items: LossControlDetailItem[]): LossControlIndicators {
  return items.reduce<LossControlIndicators>(
    (result, item) => {
      const days = differenceInCalendarDays(
        parseISO(item.expiryDate),
        new Date(),
      );
      const nextResult = { ...result, total: result.total + 1 };
      if (days < 0) nextResult.expired += 1;
      else if (days === 0) nextResult.dueToday += 1;
      else if (days <= 7) nextResult.dueIn1To7Days += 1;
      else if (days <= 15) nextResult.dueIn8To15Days += 1;
      else if (days <= 30) nextResult.dueIn16To30Days += 1;
      else nextResult.dueAfter30Days += 1;
      return nextResult;
    },
    {
      expired: 0,
      dueToday: 0,
      dueIn1To7Days: 0,
      dueIn8To15Days: 0,
      dueIn16To30Days: 0,
      dueAfter30Days: 0,
      total: 0,
    },
  );
}

function demoOperatorItems(
  branchId: string,
  accessId: string | null,
): LossControlDetailItem[] {
  const items = demoDetailItems(branchId);
  if (accessId === null) return items.slice(8);
  if (accessId === `${branchId}-demo-operador-1`) return items.slice(0, 4);
  if (accessId === `${branchId}-demo-operador-2`) return items.slice(4, 8);
  return [];
}

function filterDemoClassification(
  items: LossControlDetailItem[],
  filters: SummaryFilters,
): LossControlDetailItem[] {
  return items.filter(
    item =>
      (!filters.familyCode || item.familyCode === filters.familyCode) &&
      (!filters.groupCode || item.groupCode === filters.groupCode) &&
      (!filters.subgroupCode || item.subgroupCode === filters.subgroupCode),
  );
}

function buildDemoSummary(filters: SummaryFilters): LossControlSummary {
  const { branchId } = filters;
  const branch = demoBranches.find(item => item.id === branchId);
  const allItems = filterDemoClassification(demoDetailItems(branchId), filters);
  const operatorOne = filterDemoClassification(
    demoOperatorItems(branchId, `${branchId}-demo-operador-1`),
    filters,
  );
  const operatorTwo = filterDemoClassification(
    demoOperatorItems(branchId, `${branchId}-demo-operador-2`),
    filters,
  );
  const unassigned = filterDemoClassification(
    demoOperatorItems(branchId, null),
    filters,
  );
  const generatedAt = new Date().toISOString();

  return {
    branchId,
    branchName: branch?.name || `Loja ${branchId}`,
    generatedAt,
    sourceUpdatedAt: generatedAt,
    totalUniqueLots: allItems.length,
    totalOperators: 2,
    indicators: demoIndicators(allItems),
    operators: [
      {
        accessId: `${branchId}-demo-operador-1`,
        name: 'Ana Operadora',
        displayOrder: 0,
        indicators: demoIndicators(operatorOne),
      },
      {
        accessId: `${branchId}-demo-operador-2`,
        name: 'Carlos Operador',
        displayOrder: 1,
        indicators: demoIndicators(operatorTwo),
      },
    ],
    unassigned: {
      label: 'Sem responsavel',
      visible: unassigned.length > 0,
      displayOrder: 2,
      indicators: demoIndicators(unassigned),
    },
  };
}

function buildDemoClassifications(
  items: LossControlDetailItem[],
): LossControlFamilySummary[] {
  if (items.length === 0) return [];

  const groups = ['1', '2']
    .map(groupCode => {
      const groupItems = items.filter(item => item.groupCode === groupCode);
      return {
        groupCode,
        groupName: groupCode === '1' ? 'PERECIVEIS' : 'MERCEARIA',
        totalUniqueLots: groupItems.length,
        subgroups: [],
      };
    })
    .filter(group => group.totalUniqueLots > 0);

  return [
    {
      familyCode: '6',
      familyName: 'SUPERMERCADO',
      totalUniqueLots: items.length,
      groups,
    },
  ];
}

function buildDemoDetails(
  branchId: string,
  subjectType: 'all' | 'operator' | 'unassigned',
  accessId: string | null,
  filters: DetailsFilters,
): LossControlDetails {
  const sourceItems =
    subjectType === 'all'
      ? demoDetailItems(branchId)
      : demoOperatorItems(branchId, accessId);
  const allItems = filterDemoClassification(sourceItems, filters);
  const status = filters.status || 'all';
  const items = filterDemoItems(allItems, status);
  const summary = buildDemoSummary(filters);
  const operator = summary.operators.find(item => item.accessId === accessId);
  const generatedAt = new Date().toISOString();
  let subjectName = 'Sem responsavel';
  if (subjectType === 'all') {
    subjectName = 'Todos os produtos';
  } else if (accessId) {
    subjectName = operator?.name || 'Operador';
  }

  return {
    branchId,
    branchName: summary.branchName,
    generatedAt,
    sourceUpdatedAt: generatedAt,
    subject: {
      type: subjectType,
      accessId,
      name: subjectName,
    },
    statusFilter: status,
    totalUniqueLots: allItems.length,
    filteredTotal: items.length,
    indicators: demoIndicators(allItems),
    classifications: buildDemoClassifications(allItems),
    items,
  };
}

export const lossControlService = {
  async listBranches(demo = false): Promise<LossControlBranch[]> {
    if (demo) return demoBranches;

    const response = await api.get('/loss-control/branches');
    return normalizeBranches(response.data);
  },

  async listExpirations(
    filters: ExpirationFilters,
    demo = false,
  ): Promise<ExpirationResponse> {
    if (demo) {
      const items = filters.branchId
        ? demoItems.filter(item => item.branchId === filters.branchId)
        : demoItems;

      return {
        items,
        generatedAt: new Date().toISOString(),
        sourceUpdatedAt: new Date().toISOString(),
        sourceAvailable: true,
        demo: true,
      };
    }

    const response = await api.get('/loss-control/expirations', {
      params: filters,
    });

    return normalizeExpirations(response.data);
  },

  async listSummary(
    filters: SummaryFilters,
    demo = false,
  ): Promise<LossControlSummary> {
    if (demo) return buildDemoSummary(filters);

    const response = await api.get('/loss-control/summary', {
      params: filters,
    });
    return normalizeSummary(response.data);
  },

  async getOperatorDetails(
    accessId: string,
    filters: DetailsFilters,
    demo = false,
  ): Promise<LossControlDetails> {
    if (demo) {
      return buildDemoDetails(filters.branchId, 'operator', accessId, filters);
    }

    const response = await api.get(
      `/loss-control/operators/${accessId}/details`,
      { params: filters },
    );
    return normalizeDetails(response.data);
  },

  async getUnassignedDetails(
    filters: DetailsFilters,
    demo = false,
  ): Promise<LossControlDetails> {
    if (demo) {
      return buildDemoDetails(filters.branchId, 'unassigned', null, filters);
    }

    const response = await api.get('/loss-control/unassigned/details', {
      params: filters,
    });
    return normalizeDetails(response.data);
  },

  async getAllDetails(
    filters: DetailsFilters,
    demo = false,
  ): Promise<LossControlDetails> {
    if (demo) {
      return buildDemoDetails(filters.branchId, 'all', null, filters);
    }

    const response = await api.get('/loss-control/all/details', {
      params: filters,
    });
    return normalizeDetails(response.data);
  },

  async getDisplay(
    branchId: string,
    token: string,
    demo = false,
  ): Promise<ExpirationResponse> {
    if (demo) {
      return lossControlService.listExpirations(
        { branchId, horizonDays: 90 },
        true,
      );
    }

    const response = await apiPublic.get(`/loss-control/display/${branchId}`, {
      headers: { 'X-Display-Token': token },
    });

    return normalizeExpirations(response.data);
  },

  async getDisplaySummary(
    branchId: string,
    token: string,
    demo = false,
  ): Promise<LossControlSummary> {
    if (demo) return buildDemoSummary({ branchId });

    const response = await apiPublic.get(
      `/loss-control/display/${branchId}/summary`,
      { headers: { 'X-Display-Token': token } },
    );
    return normalizeSummary(response.data);
  },

  async createDisplayAccess(
    branchId: string,
    options: CreateDisplayAccessOptions = {},
  ): Promise<DisplayAccessResponse> {
    const response = await api.post('/loss-control/display-access', {
      branchId,
      ...(options.deviceName ? { deviceName: options.deviceName } : {}),
      ...(options.neverExpires !== undefined
        ? { neverExpires: options.neverExpires }
        : {}),
    });

    if (!isRecord(response.data) || typeof response.data.token !== 'string') {
      throw new Error('A API retornou um acesso de TV invalido.');
    }

    const token = response.data.token.trim();
    if (!token) throw new Error('A API nao retornou o token da TV.');

    const id = nullableString(response.data.id);
    const responseBranchId = nullableString(response.data.branchId);
    const deviceName = nullableString(response.data.deviceName);

    return {
      ...(id ? { id } : {}),
      ...(responseBranchId ? { branchId: responseBranchId } : {}),
      ...(deviceName ? { deviceName } : {}),
      token,
      ...(typeof response.data.neverExpires === 'boolean'
        ? { neverExpires: response.data.neverExpires }
        : {}),
      expiresAt: isValidISODate(response.data.expiresAt)
        ? response.data.expiresAt
        : null,
    };
  },

  async listDisplayDevices(
    branchId: string,
  ): Promise<LossControlDisplayDevice[]> {
    const response = await api.get('/loss-control/display-accesses', {
      params: { branchId },
    });

    return normalizeDisplayDevices(response.data);
  },

  async revokeDisplayDevice(id: string): Promise<LossControlDisplayDevice> {
    const response = await api.patch(
      `/loss-control/display-accesses/${id}/revoke`,
    );

    return normalizeDisplayDevice(response.data);
  },

  async listFamilies(
    branchId: string,
    demo = false,
  ): Promise<LossControlClassification[]> {
    if (demo) return [{ code: '6', name: 'SUPERMERCADO' }];

    const response = await api.get('/loss-control/catalog/families', {
      params: { branchId },
    });
    return normalizeClassificationsList(response.data);
  },

  async listGroups(
    branchId: string,
    familyCode: string,
    demo = false,
  ): Promise<LossControlClassification[]> {
    if (demo) {
      return [
        { code: '1', name: 'PERECIVEIS' },
        { code: '2', name: 'MERCEARIA' },
      ];
    }

    const response = await api.get('/loss-control/catalog/groups', {
      params: { branchId, familyCode },
    });
    return normalizeClassificationsList(response.data);
  },

  async listSubgroups(
    branchId: string,
    familyCode: string,
    groupCode: string,
    demo = false,
  ): Promise<LossControlClassification[]> {
    if (demo) return [];

    const response = await api.get('/loss-control/catalog/subgroups', {
      params: { branchId, familyCode, groupCode },
    });
    return normalizeClassificationsList(response.data);
  },

  async listOperatorResponsibilities(
    accessId: string,
  ): Promise<LossControlOperatorResponsibility[]> {
    const response = await api.get(
      `/loss-control/operators/${accessId}/responsibilities`,
    );
    return normalizeResponsibilities(response.data);
  },

  async syncOperatorResponsibilities(
    accessId: string,
    scopes: LossControlRequestedScope[],
  ): Promise<LossControlOperatorResponsibility[]> {
    const response = await api.put(
      `/loss-control/operators/${accessId}/responsibilities`,
      { scopes },
    );
    return normalizeResponsibilities(response.data);
  },
};
